import { hasDays, cartProductTotal } from 'lib/cart/decorators/cartProduct'
import { hasSoftClose } from 'lib/cart/decorators/softClose'

/**
Find the relevant retail cart item (if any) for the specified camper and
session.
@returns {Object} CartProduct
**/
const findRetailCartProduct = ({ camper, session, cartForm }) => {
  if (!cartForm || !cartForm.cartProducts) {
    return null
  }

  return cartForm.cartProducts.find(
    cartProduct =>
      cartProduct.session.id === session.id &&
      cartProduct.camper.id === camper.id &&
      (!cartProduct.product ||
        (cartProduct.product.published && cartProduct.product.retail))
  )
}

/**
Find the cart item (if any) for the given camper and product.
@returns {Object} cart product
**/
const findCartProduct = ({ camper, product, cartForm }) => {
  if (!cartForm || !cartForm.cartProducts) {
    return null
  }

  return cartForm.cartProducts.find(
    cartProduct =>
      cartProduct.product &&
      cartProduct.camper &&
      cartProduct.camper.id === camper.id &&
      cartProduct.product.id === product.id
  )
}

/**
Returns true if cart has any days.
@returns {boolean}
**/
function cartHasDays(cartForm) {
  return cartForm.cartProducts.some(cartProduct => hasDays(cartProduct))
}
/**
@private
@returns {object} the appropriate COPPA product for the cart
*/
const findCoppaProduct = ({ cartForm, sessions }) => {
  const cartProduct = cartForm.cartProducts.find(cartProduct =>
    hasDays(cartProduct)
  )
  const session = sessions.find(
    session => session.id === cartProduct.session.id
  )
  return session.coppaProduct
}

// find the cart product that includes the specified redeemable pass
const findRedeemablePass = ({ redeemablePass, cartForm }) => {
  if (!cartForm || !cartForm.cartProducts) {
    return null
  }

  return cartForm.cartProducts.find(cartProduct =>
    cartProduct.redeemablePasses.find(rp => rp.id === redeemablePass.id)
  )
}

/**
@private
@returns {boolean} true if cart needs a COPPA fee added
**/
function needsVerificationPayment({ cartForm, account }) {
  return (
    cartForm &&
    !account.registrationStatus.validCoppaFee &&
    cartHasDays(cartForm) &&
    cartProductsTotal({ cartForm, account }) === 0
  )
}
/**
@returns {object} the appropriate COPPA product for the cart, or null
*/
const cartCoppaProduct = ({ cartForm, account, sessions }) =>
  needsVerificationPayment({ account, cartForm })
    ? findCoppaProduct({ cartForm, sessions })
    : null

/**
Returns all cart products for the given camper and session
@returns {[Object]} cart products
**/
const findCamperSessionCartProducts = ({ camper, session, cartForm }) => {
  if (!cartForm || !cartForm.cartProducts) {
    return null
  }

  return cartForm.cartProducts.filter(
    cartProduct =>
      cartProduct.session.id === session.id &&
      cartProduct.camper.id === camper.id
  )
}

// find the subtotal for the specified camper and session
const camperSessionSubtotal = ({ camper, session, cartForm, account }) => {
  const cartProducts = findCamperSessionCartProducts({
    camper,
    session,
    cartForm
  })
  const reducer = (total, cartProduct) =>
    total + cartProductTotal(cartProduct, account)

  return cartProducts.reduce(reducer, 0)
}

// find the total for all products for the specified camper
const camperSubtotal = ({ camper, cartForm, account }) => {
  const reducer = (subtotal, cartProduct) =>
    subtotal + cartProductTotal(cartProduct, account)

  if (cartForm && cartForm.cartProducts) {
    return cartForm.cartProducts
      .filter(cartProduct => cartProduct.camper.id === camper.id)
      .reduce(reducer, 0)
  }

  return 0
}

/**
returns the total price for all products in the cart (not including COPPA fee)
*/
function cartProductsTotal({ cartForm, account }) {
  const reducer = (total, cartProduct) => {
    if (!cartProduct.product || !cartProduct.camper) {
      return total
    }

    return total + cartProductTotal(cartProduct, account)
  }

  return cartForm.cartProducts.reduce(reducer, 0)
}

// Returns the total transaction amount
function cartTotal({ account, cartForm, sessions }) {
  return (
    cartProductsTotal({ cartForm, account }) +
    cartCoppaTotal({ account, cartForm, sessions })
  )
}

const cartCoppaTotal = ({ account, cartForm, campers, sessions }) => {
  const coppaProduct = cartCoppaProduct({
    account,
    campers,
    cartForm,
    sessions
  })

  return coppaProduct ? coppaProduct.priceCents : 0
}

// form can be submitted if each cart product with days has a location
const canSubmit = ({ account, cartForm, campers, sessions }) => {
  // any soft-close restriction in the cart must be acknowledged before checkout
  if (!cartSoftCloseAcknowledged(cartForm)) {
    return false
  }

  if (cartTotal({ account, cartForm, campers, sessions }) > 0) {
    return true
  }

  return (
    cartForm &&
    cartForm.cartProducts.some(cartProduct => cartProduct.days > 0) &&
    cartForm.cartProducts
      .filter(cartProduct => cartProduct.days > 0)
      .every(cartProduct => !!cartProduct.location && !!cartProduct.product)
  )
}

/**
@returns {boolean} true if a cart product (with days) is subject to a
  soft-close restriction, evaluated against its plan.
**/
const cartProductRequiresSoftCloseAck = cartProduct =>
  cartProduct.days > 0 && hasSoftClose(cartProduct.location, cartProduct.plan)

/**
@returns {boolean} true if every cart product that requires a soft-close
  acknowledgment has been acknowledged.
**/
const cartSoftCloseAcknowledged = cartForm =>
  !cartForm ||
  !cartForm.cartProducts ||
  cartForm.cartProducts.every(
    cartProduct =>
      !cartProductRequiresSoftCloseAck(cartProduct) ||
      !!cartProduct.acknowledgedSoftClose
  )

export {
  cartCoppaProduct,
  cartHasDays,
  findRetailCartProduct,
  findCartProduct,
  findRedeemablePass,
  // cart totals
  findCamperSessionCartProducts,
  cartProductsTotal,
  cartCoppaTotal,
  camperSessionSubtotal,
  camperSubtotal,
  cartTotal,
  canSubmit,
  cartProductRequiresSoftCloseAck,
  cartSoftCloseAcknowledged
}
